import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import { COLORS, FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import { getResumeSignedUrl } from "../../../../lib/profileUploads";
import {
  addToShortlist,
  isShortlisted,
  removeFromShortlist,
} from "../../../../lib/shortlist";
import {
  getStudentHistory,
  getStudentProfile,
} from "../../../../lib/studentProfile";
import { supabase } from "../../../../lib/supabase";
import type {
  CompetitionStatus,
  StudentCompetitionHistoryRow,
  StudentProfileFull,
} from "../../../../types/database";

const THEME = PANE_THEMES.Client;

const BLACK = THEME.pageBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const CHIP_BG = "rgba(255,255,255,0.04)";

const PAGE_MAX_W = 980;

async function openInNewTab(url: string) {
  if (Platform.OS === "web") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    await Linking.openURL(url);
  }
}

function initialsOf(name: string | null | undefined): string {
  if (!name?.trim()) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function classLine(p: StudentProfileFull): string | null {
  const term = p.graduation_term?.trim() || null;
  const yr = p.graduation_year != null ? String(p.graduation_year) : null;
  const lvl = p.program_level?.trim() || null;
  const sem = term && yr ? `${term} ${yr}` : term ?? yr;
  if (lvl && sem) return `${lvl} · ${sem}`;
  return lvl ?? sem ?? null;
}

function locationLine(p: StudentProfileFull): string | null {
  const c = p.city?.trim() || null;
  const s = p.state?.trim() || null;
  if (c && s) return `${c}, ${s}`;
  return c ?? s ?? null;
}

function statusPillStyle(status: string): { bg: string; fg: string } {
  const s = status as CompetitionStatus | string;
  if (s === "open") return { bg: "rgba(143,187,138,0.14)", fg: SAGE };
  if (s === "judging") return { bg: "rgba(181,135,58,0.16)", fg: AMBER };
  if (s === "closed") return { bg: "rgba(255,255,255,0.06)", fg: WS };
  if (s === "draft") return { bg: "rgba(255,255,255,0.04)", fg: WM };
  return { bg: "rgba(255,255,255,0.04)", fg: WM };
}

const CARD_STYLE: ViewStyle = {
  backgroundColor: FOREST_MID,
  borderColor: BORDER,
  borderWidth: 1,
  borderRadius: 14,
  padding: 24,
};

function Overline({ children, color = AMBER, size = 10, spacing = 2 }: {
  children: string;
  color?: string;
  size?: number;
  spacing?: number;
}) {
  return (
    <Text
      style={{
        fontFamily: FONTS.bodyBold,
        fontSize: size,
        letterSpacing: spacing,
        textTransform: "uppercase",
        color,
      }}
    >
      {children}
    </Text>
  );
}

type PillButtonProps = {
  label: string;
  onPress: () => void;
  variant: "amber-solid" | "amber-outline" | "sage-solid";
  disabled?: boolean;
};

function PillButton({ label, onPress, variant, disabled }: PillButtonProps) {
  const isSolid = variant === "amber-solid" || variant === "sage-solid";
  const isSage = variant === "sage-solid";

  let bg: string;
  if (isSolid) {
    if (isSage) {
      bg = AMBER; // base — overridden by hover/press below
    } else {
      bg = AMBER;
    }
  } else {
    bg = "transparent";
  }

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      style={({ hovered, pressed }) => ({
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isSolid
          ? isSage
            ? pressed
              ? COLORS.SAGE.hex
              : hovered
              ? COLORS.SAGE.hex
              : SAGE
            : pressed
            ? THEME.accentDeep
            : hovered
            ? THEME.accent
            : AMBER
          : pressed
          ? "rgba(181,135,58,0.20)"
          : hovered
          ? "rgba(181,135,58,0.12)"
          : "transparent",
        borderWidth: isSolid ? 0 : 1,
        borderColor: isSolid ? "transparent" : AMBER,
        opacity: disabled ? 0.5 : 1,
      })}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 12,
          letterSpacing: 0.4,
          color: isSolid ? THEME.pageBg : AMBER,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export default function ClientStudentProfileScreen() {
  const params = useLocalSearchParams<{ id: string; competitionId?: string }>();
  const studentId = typeof params.id === "string" ? params.id : null;
  const urlCompetitionId =
    typeof params.competitionId === "string" ? params.competitionId : null;

  const [profile, setProfile] = useState<StudentProfileFull | null>(null);
  const [history, setHistory] = useState<StudentCompetitionHistoryRow[]>([]);
  const [resumeUri, setResumeUri] = useState<string | null>(null);
  const [primaryCompetitionId, setPrimaryCompetitionId] = useState<string | null>(null);
  const [shortlistEntryId, setShortlistEntryId] = useState<string | null>(null);
  const [shortlistBusy, setShortlistBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const targetCompetitionId = urlCompetitionId ?? primaryCompetitionId;

  const load = useCallback(async () => {
    if (!studentId) return;
    setErr(null);
    setLoading(true);
    try {
      // Pull profile + history in parallel; fan out resume + shortlist target after.
      const [p, h, regsRes] = await Promise.all([
        getStudentProfile(studentId),
        getStudentHistory(studentId),
        urlCompetitionId
          ? Promise.resolve(null)
          : supabase
              .from("registrations")
              .select("competition_id, created_at")
              .eq("student_id", studentId)
              .eq("status", "registered")
              .order("created_at", { ascending: false })
              .limit(1)
              .maybeSingle(),
      ]);

      setProfile(p);
      setHistory(
        [...h].sort((a, b) => {
          const ta = a.closes_at ? new Date(a.closes_at).getTime() : 0;
          const tb = b.closes_at ? new Date(b.closes_at).getTime() : 0;
          return tb - ta;
        })
      );

      // Resume signed URL.
      if (p?.resume_url) {
        setResumeUri(await getResumeSignedUrl(p.resume_url));
      } else {
        setResumeUri(null);
      }

      // Resolve primary competition for shortlist target if no URL param.
      let target = urlCompetitionId;
      if (!target) {
        const primary =
          regsRes && !regsRes.error && regsRes.data
            ? (regsRes.data.competition_id as string | null)
            : null;
        setPrimaryCompetitionId(primary);
        target = primary;
      }

      if (target && p) {
        const entry = await isShortlisted(target, studentId);
        setShortlistEntryId(entry?.id ?? null);
      } else {
        setShortlistEntryId(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [studentId, urlCompetitionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const subLine = useMemo(() => {
    if (!profile) return "";
    const parts: string[] = [];
    if (profile.university) parts.push(profile.university);
    if (profile.major) parts.push(profile.major);
    if (profile.graduation_year != null) parts.push(`Class of ${profile.graduation_year}`);
    const base = parts.join(" · ");
    if (profile.program_level) return `${base} (${profile.program_level})`;
    return base;
  }, [profile]);

  async function onToggleShortlist() {
    if (!studentId || shortlistBusy) return;
    setShortlistBusy(true);
    try {
      if (shortlistEntryId) {
        await removeFromShortlist(shortlistEntryId);
        setShortlistEntryId(null);
      } else if (targetCompetitionId) {
        await addToShortlist({
          competitionId: targetCompetitionId,
          studentId,
        });
        const entry = await isShortlisted(targetCompetitionId, studentId);
        setShortlistEntryId(entry?.id ?? null);
      } else {
        console.warn(
          "[ClientStudent] no target competition for shortlist toggle"
        );
      }
    } catch (e) {
      console.warn("[ClientStudent] shortlist toggle failed:", e);
    } finally {
      setShortlistBusy(false);
    }
  }

  function onOpenResume() {
    if (resumeUri) void openInNewTab(resumeUri);
  }

  if (loading) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          paddingVertical: 80,
          backgroundColor: BLACK,
        }}
      >
        <ActivityIndicator size="large" color={AMBER} />
      </View>
    );
  }

  if (!profile || !studentId) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: BLACK,
          paddingHorizontal: 32,
          paddingVertical: 28,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 24,
            color: LINEN,
          }}
        >
          Student not found
        </Text>
        <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 13, color: WS }}>
          This student is not visible to your company.
        </Text>
      </View>
    );
  }

  const fullName = profile.full_name ?? "Candidate";
  const firstName = fullName.split(/\s+/)[0] ?? "They";
  const fields = profile.fields_of_competency ?? [];
  const hasContact =
    !!profile.email || !!profile.phone || !!profile.linkedin_url;

  const shortlistLabel = shortlistEntryId
    ? "Remove from shortlist"
    : "Shortlist";

  const shortlistDisabled = !targetCompetitionId && !shortlistEntryId;

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: BLACK }}
      contentContainerStyle={{ paddingBottom: 60 }}
    >
      <View
        style={{
          paddingHorizontal: 32,
          paddingVertical: 28,
          maxWidth: PAGE_MAX_W,
          width: "100%",
          alignSelf: "stretch",
          gap: 18,
        }}
      >
        {/* HERO ROW */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <View style={{ flex: 1, minWidth: 260 }}>
            <Overline spacing={3}>Candidate profile</Overline>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.displayMedium,
                fontSize: 30,
                lineHeight: 36,
                color: LINEN,
              }}
            >
              {fullName}
            </Text>
            {subLine ? (
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  lineHeight: 20,
                  color: WS,
                }}
              >
                {subLine}
              </Text>
            ) : null}
          </View>

          <View style={{ gap: 8, alignItems: "stretch", minWidth: 180 }}>
            <PillButton
              label={shortlistBusy ? "…" : shortlistLabel}
              onPress={onToggleShortlist}
              variant={shortlistEntryId ? "sage-solid" : "amber-solid"}
              disabled={shortlistBusy || shortlistDisabled}
            />
            {profile.resume_url && resumeUri ? (
              <PillButton
                label="Open resume →"
                onPress={onOpenResume}
                variant="amber-outline"
              />
            ) : null}
          </View>
        </View>

        {err ? (
          <View
            style={{
              borderColor: "rgba(220,80,80,0.40)",
              borderWidth: 1,
              backgroundColor: "rgba(220,80,80,0.08)",
              borderRadius: 10,
              paddingHorizontal: 14,
              paddingVertical: 10,
            }}
          >
            <Text style={{ color: "rgba(255,180,180,0.92)", fontFamily: FONTS.body, fontSize: 13 }}>
              {err}
            </Text>
          </View>
        ) : null}

        {/* AVATAR + CONTACT CARD */}
        <View
          style={[
            CARD_STYLE,
            {
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 28,
              flexWrap: "wrap",
            },
          ]}
        >
          <View style={{ alignItems: "center", justifyContent: "flex-start" }}>
            {profile.avatar_url ? (
              <Image
                source={{ uri: profile.avatar_url }}
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 44,
                  borderWidth: 1,
                  borderColor: BORDER,
                  backgroundColor: FOREST_MID,
                }}
              />
            ) : (
              <View
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 44,
                  borderWidth: 1,
                  borderColor: BORDER,
                  backgroundColor: FOREST_MID,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.displayMedium,
                    fontSize: 28,
                    color: AMBER,
                  }}
                >
                  {initialsOf(fullName)}
                </Text>
              </View>
            )}
          </View>

          <View style={{ flex: 1, minWidth: 220, gap: 14 }}>
            {!hasContact && !locationLine(profile) && !classLine(profile) ? (
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: WL,
                  fontStyle: "italic",
                }}
              >
                No public contact info yet.
              </Text>
            ) : (
              <>
                {profile.email ? (
                  <ContactRow label="Email">
                    <LinkText
                      href={`mailto:${profile.email}`}
                      label={profile.email}
                    />
                  </ContactRow>
                ) : null}
                {profile.phone ? (
                  <ContactRow label="Phone">
                    <LinkText
                      href={`tel:${profile.phone}`}
                      label={profile.phone}
                    />
                  </ContactRow>
                ) : null}
                {profile.linkedin_url ? (
                  <ContactRow label="LinkedIn">
                    <Pressable onPress={() => void openInNewTab(profile.linkedin_url!)}>
                      <Text
                        style={{
                          fontFamily: FONTS.bodyMedium,
                          fontSize: 14,
                          color: AMBER,
                          textDecorationLine: "underline",
                        }}
                      >
                        View profile →
                      </Text>
                    </Pressable>
                  </ContactRow>
                ) : null}
                {locationLine(profile) ? (
                  <ContactRow label="Location">
                    <Text
                      style={{
                        fontFamily: FONTS.bodyMedium,
                        fontSize: 14,
                        color: LINEN,
                      }}
                    >
                      {locationLine(profile)}
                    </Text>
                  </ContactRow>
                ) : null}
                {classLine(profile) ? (
                  <ContactRow label="Class">
                    <Text
                      style={{
                        fontFamily: FONTS.bodyMedium,
                        fontSize: 14,
                        color: LINEN,
                      }}
                    >
                      {classLine(profile)}
                    </Text>
                  </ContactRow>
                ) : null}
              </>
            )}
          </View>
        </View>

        {/* ABOUT */}
        <View style={CARD_STYLE}>
          <Overline>About</Overline>
          <Text
            style={{
              marginTop: 10,
              fontFamily: profile.bio
                ? FONTS.bodyLight
                : FONTS.body,
              fontSize: 14,
              lineHeight: 26,
              color: profile.bio ? WS : WL,
              fontStyle: profile.bio ? "normal" : "italic",
            }}
          >
            {profile.bio ?? `${firstName} hasn't added a bio yet.`}
          </Text>
        </View>

        {/* FIELDS */}
        <View style={CARD_STYLE}>
          <Overline>Fields of competency</Overline>
          {fields.length === 0 ? (
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: WL,
                fontStyle: "italic",
              }}
            >
              Not specified yet.
            </Text>
          ) : (
            <View
              style={{
                marginTop: 12,
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {fields.map((f) => (
                <View
                  key={f}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 3,
                    backgroundColor: CHIP_BG,
                    borderWidth: 1,
                    borderColor: BORDER,
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyMedium,
                      fontSize: 11,
                      color: LINEN,
                    }}
                  >
                    {f}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* HISTORY */}
        <View style={CARD_STYLE}>
          <Overline>Competition history</Overline>
          {history.length === 0 ? (
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: WL,
                fontStyle: "italic",
              }}
            >
              No completed competitions yet.
            </Text>
          ) : (
            <View style={{ marginTop: 14, gap: 10 }}>
              {history.map((row) => {
                const sp = statusPillStyle(String(row.competition_status));
                return (
                  <Pressable
                    key={row.competition_id}
                    onPress={() =>
                      router.push(
                        `/client/competition/${row.competition_id}/dashboard` as Href
                      )
                    }
                    style={({ hovered, pressed }) => ({
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: hovered || pressed ? "rgba(181,135,58,0.35)" : BORDER,
                      backgroundColor:
                        hovered || pressed ? "rgba(255,255,255,0.02)" : "transparent",
                      paddingHorizontal: 14,
                      paddingVertical: 12,
                    })}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontFamily: FONTS.displayMedium,
                            fontSize: 16,
                            color: LINEN,
                          }}
                        >
                          {row.competition_name}
                        </Text>
                        <Text
                          style={{
                            marginTop: 2,
                            fontFamily: FONTS.body,
                            fontSize: 11,
                            color: WS,
                          }}
                        >
                          {row.company_name}
                        </Text>
                        <Text
                          style={{
                            marginTop: 8,
                            fontFamily: FONTS.body,
                            fontSize: 12,
                            color: WS,
                          }}
                        >
                          Submitted {row.submitted_count} of {row.total_deliverables} deliverables
                          {row.team_name ? ` · Team ${row.team_name}` : ""}
                        </Text>
                      </View>
                      <View style={{ alignItems: "flex-end", gap: 6 }}>
                        <View
                          style={{
                            paddingHorizontal: 9,
                            paddingVertical: 3,
                            borderRadius: 999,
                            backgroundColor: sp.bg,
                          }}
                        >
                          <Text
                            style={{
                              fontFamily: FONTS.bodyMedium,
                              fontSize: 10,
                              letterSpacing: 0.6,
                              textTransform: "uppercase",
                              color: sp.fg,
                            }}
                          >
                            {String(row.competition_status)}
                          </Text>
                        </View>
                        <Text
                          style={{
                            fontFamily: FONTS.bodyMedium,
                            fontSize: 12,
                            color: row.placement != null ? SAGE : WM,
                          }}
                        >
                          {row.placement != null
                            ? `Placed #${row.placement}`
                            : "Pending"}
                        </Text>
                      </View>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

function ContactRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: WL,
        }}
      >
        {label}
      </Text>
      <View style={{ marginTop: 4 }}>{children}</View>
    </View>
  );
}

function LinkText({ href, label }: { href: string; label: string }) {
  return (
    <Pressable onPress={() => void Linking.openURL(href)}>
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 14,
          color: AMBER,
          textDecorationLine: "underline",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}
